import { useEffect, useMemo, useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import Loader from '../../components/Loader/Loader';
import { useTable, useSortBy, useGlobalFilter } from 'react-table';
import { WhiteCardContainer } from '../../components';
import FormButton from '../../components/formElements/FormButton';
import { PageContentService } from '../../services/Description';

const QuienesSomos = () => {
    const [fullList, setFullList] = useState([]);
    const [refresh, setRefresh] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [dialog, setDialog] = useState({ isOpen: false, mode: '' });

    const [uniqueKey, setUniqueKey] = useState('');
    const [fieldName, setFieldName] = useState('');
    const [fieldValue, setFieldValue] = useState('');

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');

    const [error, setError] = useState({
        fieldName: '',
        fieldValue: ''
    });

    const activePage = 'quienes-somos';

    // FETCH
    useEffect(() => {
        setIsLoading(true);
        PageContentService.getList()
            .then((res) => {
                setFullList(res.data);
                setIsLoading(false);
            })
            .catch(() => setIsLoading(false));
    }, [refresh]);

    const pageData = useMemo(() => {
        return fullList.find((item) => item.pageType === activePage) || null;
    }, [fullList]);

    // TABLE ROWS
    const rows = useMemo(() => {
        if (!pageData) return [];

        return pageData.fields.map((f) => ({
            col1: f.fieldName,
            col2: f.fieldValue,
            col3: (
                <div
                    onClick={() =>
                        openModal('edit', {
                            fieldName: f.fieldName,
                            fieldValue: f.fieldValue,
                            uniqueKey: f.uniqueyKey
                        })
                    }
                    className="text-dl-primary-900 hover:text-dl-accent cursor-pointer"
                >
                    Editar
                </div>
            )
        }));
    }, [pageData]);

    const columns = useMemo(
        () => [
            { Header: 'Nombre del Campo', accessor: 'col1' },
            { Header: 'Valor del Campo', accessor: 'col2' },
            { Header: 'Acciones', accessor: 'col3' }
        ],
        []
    );

    const tableInstance = useTable({ columns, data: rows }, useGlobalFilter, useSortBy);

    const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows: tableRows } = tableInstance;

    // MODAL
    const openModal = (mode, item = null) => {
        setDialog({ isOpen: true, mode });

        if (mode === 'edit' && item) {
            setUniqueKey(item.uniqueKey);
            setFieldName(item.fieldName);
            setFieldValue(item.fieldValue);
        }

        if (mode === 'image') {
            setImagePreview(pageData?.image ? `${process.env.REACT_APP_API_SERVER}/${pageData.image}` : '');
            setImageFile(null);
        }

        setError({ fieldName: '', fieldValue: '' });
    };

    const closeModal = () => {
        setDialog({ isOpen: false, mode: '' });
        setUniqueKey('');
        setImagePreview('');
        setImageFile(null);
    };

    // SAVE FIELD (uniqueKey based)
    const handleSubmit = async () => {
        if (!fieldName.trim()) {
            setError({ fieldName: 'Requerido' });
            return;
        }

        if (!pageData) return;

        try {
            const updatedFields = pageData.fields.map((f) =>
                f.uniqueyKey === uniqueKey ? { ...f, fieldName, fieldValue } : f
            );

            const formData = new FormData();
            formData.append('id', pageData.id);
            formData.append('fields', JSON.stringify(updatedFields));

            await PageContentService.update(formData);

            setRefresh((prev) => !prev);
            closeModal();
        } catch (e) {
            console.error(e);
        }
    };

    // SAVE IMAGE
    const handleImageSave = async () => {
        if (!imageFile || !pageData) return;

        const formData = new FormData();
        formData.append('id', pageData.id);
        formData.append('image', imageFile);

        await PageContentService.update(formData);

        setRefresh((prev) => !prev);
        closeModal();
    };

    if (isLoading) return <Loader />;

    return (
        <WhiteCardContainer>
            <div className="px-4 sm:px-6 lg:px-8 mb-10">
                <div className="mt-6 gap-6 my-4 flex justify-between items-center">
                    <h2 className="text-xl font-semibold">¿Quiénes somos?</h2>
                </div>

                {/* IMAGE SECTION */}
                <div className="mb-10 flex flex-col items-center text-center">
                    <h3 className="text-lg font-semibold mb-4">Imagen principal</h3>

                    {pageData?.image ? (
                        <img
                            src={`${process.env.REACT_APP_API_SERVER}/${pageData.image}`}
                            alt="Page"
                            className="h-44 w-44 rounded-full object-cover shadow-md border"
                        />
                    ) : (
                        <p className="text-gray-500">No hay imagen subida.</p>
                    )}

                    <button
                        onClick={() => openModal('image')}
                        className="mt-4 px-5 py-2 bg-dl-primary-700 text-white rounded-md"
                    >
                        Actualizar Imagen
                    </button>
                </div>

                {/* TABLE */}
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                    <table {...getTableProps()} className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                            {headerGroups.map((hg) => (
                                <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                                    {hg.headers.map((col) => (
                                        <th
                                            {...col.getHeaderProps(col.getSortByToggleProps())}
                                            key={col.id}
                                            className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold"
                                        >
                                            <div className="inline-flex items-center">
                                                {col.render('Header')}
                                                {col.isSorted &&
                                                    (col.isSortedDesc ? (
                                                        <ChevronUpIcon className="h-5 w-5" />
                                                    ) : (
                                                        <ChevronDownIcon className="h-5 w-5" />
                                                    ))}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>

                        <tbody {...getTableBodyProps()} className="divide-y divide-gray-200 bg-white">
                            {tableRows.map((row) => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()} key={row.id}>
                                        {row.cells.map((cell) => (
                                            <td
                                                {...cell.getCellProps()}
                                                key={cell.column.id}
                                                className="px-3 py-4 text-sm text-gray-500 whitespace-normal"
                                            >
                                                {cell.render('Cell')}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* EDIT MODAL */}
            {dialog.isOpen && dialog.mode === 'edit' && (
                <Transition appear show as={Fragment}>
                    <Dialog as="div" className="relative z-10" onClose={closeModal}>
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                        <div className="fixed inset-0 flex items-center justify-center p-4">
                            <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                                <Dialog.Title className="text-lg font-medium">Editar Campo</Dialog.Title>

                                <div className="mt-4 space-y-4">
                                    <input
                                        value={fieldName}
                                        onChange={(e) => setFieldName(e.target.value)}
                                        className="w-full border rounded-md px-3 py-2"
                                    />
                                    <textarea
                                        rows={3}
                                        value={fieldValue}
                                        onChange={(e) => setFieldValue(e.target.value)}
                                        className="w-full border rounded-md px-3 py-2"
                                    />

                                    <div className="flex justify-end gap-3">
                                        <FormButton label="Cancelar" onClick={closeModal} />
                                        <FormButton label="Guardar" onClick={handleSubmit} />
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </div>
                    </Dialog>
                </Transition>
            )}

            {/* IMAGE MODAL */}
            {dialog.isOpen && dialog.mode === 'image' && (
                <Transition appear show as={Fragment}>
                    <Dialog as="div" className="relative z-10" onClose={closeModal}>
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                        <div className="fixed inset-0 flex items-center justify-center p-4">
                            <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                                <Dialog.Title className="text-lg font-medium">Actualizar Imagen</Dialog.Title>

                                <div className="mt-4 flex flex-col items-center">
                                    {imagePreview && (
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            className="h-40 w-40 rounded-full object-cover mb-3"
                                        />
                                    )}

                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            setImageFile(file);
                                            setImagePreview(URL.createObjectURL(file));
                                        }}
                                    />

                                    <div className="flex justify-end gap-3 mt-4 w-full">
                                        <FormButton label="Cancelar" onClick={closeModal} />
                                        <FormButton label="Guardar" onClick={handleImageSave} />
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </div>
                    </Dialog>
                </Transition>
            )}
        </WhiteCardContainer>
    );
};

export default QuienesSomos;
